import { useState } from 'react'
import { Eye } from 'lucide-react'
import { FieldRenderer } from '@/features/contract/components/FieldRenderer'
import type { FormSchema, FormSection } from '../types'

type Props = {
  schema: FormSchema
  section: FormSection
}

function clampWidth(w: number | undefined): number {
  if (!w || w < 1) return 12
  if (w > 12) return 12
  return w
}

/**
 * Renderuje sekcję tak, jak będzie wyglądać w realnym formularzu kontraktu -
 * wykorzystując FieldRenderer (mode='EDIT') i siatkę 12-kolumnową.
 * Wartości trzymane lokalnie, nic nie jest zapisywane.
 */
export function SectionPreview({ schema, section }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>({})

  const fieldById = new Map(schema.fields.map(f => [f.id, f]))
  const fields = (schema.placements ?? [])
    .filter(p => p.sectionId === section.id)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(p => {
      const field = fieldById.get(p.fieldId)
      return field ? { field, colWidth: p.colWidth } : null
    })
    .filter((x): x is { field: NonNullable<ReturnType<typeof fieldById.get>>; colWidth: number } => x !== null)

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="px-4 py-3 border-b bg-white flex items-center gap-2 text-xs text-slate-600">
        <Eye size={14} className="text-sky-600" />
        <span>
          Podgląd sekcji <strong className="text-slate-900">{section.labelPl}</strong> -
          interaktywny, ale wartości nie są zapisywane.
        </span>
      </div>
      <div className="p-4 md:p-6">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-6 py-6">
          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Brak pól w tej sekcji.</p>
          ) : (
            <div className="grid grid-cols-12 gap-x-6 gap-y-5">
              {fields.map(({ field: f, colWidth }) => (
                <div key={f.fieldCode} style={{ gridColumn: `span ${clampWidth(colWidth)}` }}>
                  <FieldRenderer
                    field={f}
                    value={values[f.fieldCode]}
                    mode="EDIT"
                    onChange={(v) => setValues(prev => ({ ...prev, [f.fieldCode]: v }))}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
